import _ from 'lodash'
import template from './column-data-source-form.hbs'
import reportConstants from 'scoreboard/reports/report-constants'
import StackForm from './stack-form'
import StandardSourceSelector from 'scoreboard/components/data-source-selector/standard-source-selector'
import ColumnEquation from './column-equation'
import ReportDataSourceForm from './designer-data-source-form'
import i18n from 'scoreboard/components/i18n'
import * as AggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
const { listUniqueValues } = AggregationTypeOptions
import DataType from 'scoreboard/datasets/model/data-type'
const { CALENDAR_PERIOD } = DataType
import ReportRepeatBy from 'scoreboard/reports/common/model/designer-repeat-by'
import { isColumnScored } from 'scoreboard/reports/scorecard-report/scorecard-report-columns'
import constants from 'scoreboard/components/constants'

const ColumnDataSourceForm = StackForm.extend({

	className: 'column-data-source-form field-form',

	/**
	 * View for editing the data source of a stack of a designer report
	 *
	 * @param {ReportStack} options.model the model of a Report Stack
	 * @param {ReportModel} options.reportModel the model of the report
	 * @param {ReportTableModel} options.tableModel the model of the table
	 * @param {ReportColumn} options.column the model of one of a column in a stack
	 */
	initialize: function(options = {}) {
		StackForm.prototype.initialize.apply(this, arguments)

		//compute the list of options available to change to
		this.optionData = this.optionData()

		this.initialColumnType = this.column.get('columnType')
		this._initialLabel = this.column.get('label')
		this._dataSourceType = this.column.get('calculated') ? reportConstants.SOURCE_CALCULATED
			: reportConstants.SOURCE_BASIC
		//for changes we don't need to fetch in this view because we're not
		//showing any results in the form itself
		this.reportModel.noFetch = true
	},

	optionData: function() {
		let opts = {
			forColumn: true,
			currentFieldIdOrColType: this.column.fieldIdOrColumnType()
		}
		//build the option list from the original report model since that is
		//the current state of the report
		return this.options.sourceReportModel.columnOptionsList(this.model, opts)
			.filter(opt => !this.column.isGrouping() || opt.canGroupBy)
	},

	render: function() {
		this.$el.html(template(this.options))

		//dropdown to switch from basic to calculated types
		let { optionData, reportModel } = this

		if (reportModel.reportDomainType === 'dataset') {
			this.$('.dialog-body').addClass('dark')
		}

		const disallowTypeToggle =
			//if we're grouping don't allow the change to a calculated column
			this.column.isGrouping() ||
			// #38697 Calculated values are not allowed for scorecards and initiatives
			_.includes([
				'initiative', 'scorecard-item'
			], reportModel.reportDomainType)

		_.each(optionData, o => o.label = i18n.get(o.label))
		let selectorOptions = {
			domainType: reportModel.reportDomainType,
			dataPropertyOptions: _.sortBy(optionData, 'label'),
			// The data source for a report stack applies to every domain object
			// in the report, so a specific domain object won't be selected
			allowDomainObjectSelection: false,
			disallowTypeToggle,
			createEquationEditor: ({ equation }) => {
				return new ColumnEquation({
					model: this.reportModel,
					equation,
					optionData: _.filter(this.optionData, data => !data.disableCalculation),
					label: this.column.get('label')
				})
			},
			dataSource: _.defaults({
				dataProperty: this.column.fieldIdOrColumnType(),
				dataSourceType: this._dataSourceType
			}, this.column.attributes),
			// #41424 percentage not supported yet
			excludedAggregationTypes: ['percentage']
		}

		this._renderSelector(selectorOptions)

		// #40863 Grouping on calculated columns is disallowed, so don't even show
		// the calculated/manual toggle.
		const isGroupedColumn = !_.isNil(this.column.get('groupingId'))
		this.$('.labeled-toggle-and-label').toggleClass('is-not-displayed',
			isGroupedColumn)

		// #54338 can't add filters if the report is not aggregating
		const isAggregatedReport = this.reportModel.isAggregating()
		this.$('.filters').toggleClass('is-not-displayed',
			isGroupedColumn || !isAggregatedReport)

		return StackForm.prototype.render.apply(this, arguments)
	},

	_renderSelector: function (selectorOptions) {
		this._selectorType = selectorOptions.domainType
		this._removeSelector()
		if (this._selectorType === 'scorecard-item' || this._selectorType === 'initiative') {
			this._createStandardSelector(selectorOptions)
		} else if (this._selectorType === 'dataset') {
			this._createDatasetSelector(selectorOptions)
		}
		this.$('.dialog-body-content').append(this.sourceSelector.el)
		this.sourceSelector.render()
		this._determineAggregationTypes()
	},


	/**
	 * Form used to add or edit an aggregation (called a Stack in the UI)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the starting Dataset
	 *
	 * @param {Object} [options.table] the dataset table the stack belongs to,
	 * if applicable. Used when working with percentage aggregations and to
	 * determine if it's appropriate to show the aggregation type dropdown
	 *
	 * @param {Object} options.dataSource the data source being edited
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 *
	 * @param {String} [options.allowAppCalendar] whether "Current Calendar"
	 * should be an option when working with date filters
	 *
	 * @param {Dataset[]} options.datasets all the top-level datasets the
	 * user has permission to view
	 *
	 * @param {Boolean} [options.allLinkedFields=false] whether to show all
	 * linked fields rather than just the explicitly defined visible linked
	 * fields when building filters
	 *
	 * @param {FormView} options.formView - the FormView that manages this view
	 * (and potentially other views)
	 */
	_createDatasetSelector: function (selectorOptions) {
		let dataset = this.reportModel.get('dataset')
		let { dataSource } = selectorOptions
		dataSource = _.cloneDeep(dataSource)
		dataSource.aggregations = dataSource.datasetAggregations || []
		dataSource.filters = dataSource.datasetFilters
		dataSource.fieldId = dataSource.datasetFieldId
		//#46341 disallow calendar-period for calculated columns
		let excludedDataTypes = [CALENDAR_PERIOD]
		if (dataSource.dataSourceType === reportConstants.SOURCE_BASIC) {
			delete dataSource.equation
		}

		this.sourceSelector = new ReportDataSourceForm({
			model: dataset,
			formView: this.formView,
			dataSource,
			optionData: this.optionData,
			startingDatasetId: dataset.get('rootDatasetId'),
			hideAggregationTypeForSameDataset: true,
			reportModel: this.reportModel,
			treeItemsById: this.reportModel?.treeItemsById,
			useStartingDataset: true,
			excludedDataTypes,
			individualRecords: this.reportModel.get('showIndividualRecords'),
			isGrouping: this.column.isGrouping()
		})
		//the datasets data source form handles all the logic internally and outputs
		//the end state on the "done" event when the form is submitted, so we
		//need to grab the data at that point
	},

	_createStandardSelector: function (selectorOptions) {
		this.sourceSelector = new StandardSourceSelector(selectorOptions)
		this.sourceSelector.$el.addClass('margin-collapse')

		// Listen for any changes in the selector and apply appropriately to
		// the report stack
		this.listenTo(this.sourceSelector, 'change:dataSourceType', () => {
			let { dataSourceType, dataProperty } = this.sourceSelector.val()
			this.column.set('source', dataSourceType)
			if (dataSourceType == reportConstants.SOURCE_BASIC) {
				this._changeBasicSourceColumn(dataProperty)
			} else {
				//use the previous equation if any
				if (this._calculatedMeta) {
					this.column.set(this._calculatedMeta)
				}
			}
		})

		this.listenTo(this.sourceSelector, 'change:dataProperty', () => {
			let newColumnType = this.sourceSelector.val().dataProperty
			this._changeBasicSourceColumn(newColumnType)
		})

		this.listenTo(this.sourceSelector, 'change:equation',
			this._changeEquation.bind(this))

		this.listenTo(this.sourceSelector, 'change:aggregationType',
			this._changeAggregationType.bind(this))
	},

	_changeBasicSourceColumn: function(newFieldIdOrColumnType) {
		this._dataSourceType = reportConstants.SOURCE_BASIC
		let propsToKeep = { id : this.column.id, calculated: false }
		let colorType = this.column.get('colorType')
		if (!_.isNil(colorType)) {
			propsToKeep['colorType'] = colorType
			propsToKeep['thresholdColoring'] = this.column.get('thresholdColoring')
		}
		let columnData = this.reportModel._defaultColumnData(newFieldIdOrColumnType, propsToKeep)
		if (columnData != null) {
			this.column.set(columnData)
		}
		this._determineAggregationTypes()
	},

	_determineAggregationTypes: function() {
		let showAggregationType = this.reportModel.requiresAggregationType(
			this.column.fieldIdOrColumnType(), { column: this.column })
			&& this._dataSourceType == reportConstants.SOURCE_BASIC
		let { aggregationType, dataType, columnType} = this.column.attributes
		if (this.sourceSelector instanceof ReportDataSourceForm) {
			this.sourceSelector.dataTypeSelector.setDataType(dataType)
			if (showAggregationType) {
				this.sourceSelector.aggregationTypeDropdown.val(aggregationType)
				this.sourceSelector.renderAggregationType()
			}
			this.sourceSelector.toggleAggregationType()
			return
		}
		// for the StandardSourceSelector
		this.sourceSelector.renderAggregationType(showAggregationType,
			aggregationType, dataType, getExcludedAggregationTypes(columnType))	
	},

	_changeEquation: function(equation) {
		this._dataSourceType = reportConstants.SOURCE_CALCULATED
		let newColumnData = _.defaults({
			equation,
			calculated: true,
			source: this._dataSourceType
		}, this.column.attributes)
		//silent for now to prevent request since we're not showing a table with the changes
		this.column.set(newColumnData, {silent: true})

		//keep a cached version, to ensure changing from calculated to basic
		//doesn't destroy the previous calculation setting if changed back
		this._calculatedMeta = this.column.clone()

		//let the form view know we changed the equation
		this.formView.change()
		this.formView.validate()
	},

	_changeAggregationType: function(aggregationType) {
		this.column.set('aggregationType', aggregationType)
	},

	test: function() {
		let err = StackForm.prototype.test.apply(this, arguments)
		if (this._selectorType === 'dataset') {
			err = err || this.sourceSelector.test()
		}
		return err
	},

	_finalize: function() {
		let usingDefaultLabel = !this.column.get('customLabel')
		if (this._selectorType === 'dataset') {
			//for datasets, the selector will give us the end state to set.
			this.listenToOnce(this.sourceSelector, 'done', dataSource => {
				let datasetFieldId
				if (dataSource.dataSourceType == reportConstants.SOURCE_BASIC) {
					const noFilters =
						_.isEmpty(this.column.get('datasetFilters'))
						&& _.isEmpty(dataSource.filters)

					const sameAggregationType =
						dataSource.aggregationType == this.column.get('aggregationType')
					
					const sameField =
						dataSource.fieldId == this.column.get('datasetFieldId')
					if (sameField && noFilters && sameAggregationType) {
						return	//#47120 no changes, no-op
					}

					this._dataSourceType = reportConstants.SOURCE_BASIC
					this.column.unset('equation')
					let field = this.reportModel.get('dataset').getFieldByIds(dataSource)
					if (usingDefaultLabel) {
						this.column.set('label', field.label)
					}
					datasetFieldId = dataSource.fieldId
				} else {
					this._dataSourceType = reportConstants.SOURCE_CALCULATED
					this.column.set('equation', dataSource.equation)
					if (usingDefaultLabel) {
						this.column.set('label', dataSource.equation)
					}
					datasetFieldId = null
				}
				let updateProps = {
					calculated: dataSource.dataSourceType !== reportConstants.SOURCE_BASIC,
					dataType: dataSource.dataType,
					coercedDataType: dataSource.dataType,
					aggregationType: dataSource.aggregationType,
					datasetFieldId,
					visibleLinkedFieldId: dataSource.visibleLinkedFieldId,
					datasetAggregations: [].concat(dataSource.aggregations || []),
					datasetFilters: [].concat(dataSource.filters || [])
				}
				if (datasetFieldId) {
					// #41069 In the event of a change to a dataset field, copy over the
					// field attributes the same way we do when a column is created.
					updateProps = this.reportModel._defaultColumnData(datasetFieldId,
						updateProps)
				}

				// don't set the default column label if a custom label has
				// been set
				if (!usingDefaultLabel) delete updateProps.label
				this.column.set(updateProps)
			})
			//manually call the submit on the selector since our parent class
			//is providing submit
			return this.sourceSelector.submit()
		} else if (this._selectorType === 'scorecard-item') {
			const { model, reportModel } = this.options
			toggleScoredBreakdown({
				stack: model,
				report: reportModel,
				column: this.column,
				oldColumnType: this.initialColumnType
			})
		}
		if (usingDefaultLabel) {
			this._initialLabel = this.reportModel.defaultLabel(this.column)
		}
		this.column.set('label', this._initialLabel)
	},

	_removeSelector() {
		if (this.sourceSelector) {
			this.stopListening(this.sourceSelector)
			this.sourceSelector.remove()
		}
		delete this.sourceSelector
	},

	remove: function() {
		StackForm.prototype.remove.apply(this, arguments)
		this._removeSelector()
	}
})

const toggleScoredBreakdown = ({ stack, report, column, oldColumnType }) => {
	const oldScored = isColumnScored(oldColumnType)
	const newScored = isColumnScored(column.get('columnType'))
	const scoredToNonScoredColumn = oldScored && !newScored
	const nonScoredToScoredColumn = !oldScored && newScored

	const stackIdx = report.stacks().findIndex(stack)
	if (scoredToNonScoredColumn) {
		const columns = stack.columns()
		if (columns.length == 1) stack.unset('repeatBy')
		else {
			columns.remove(column)
			report.addStack(stackIdx + 1, column)
		}
	} else if (nonScoredToScoredColumn) {
		const columnData = report._defaultStackData(column.get('columnType'))
		const repeatBy = new ReportRepeatBy(columnData.repeatBy, { parse: true })
		stack.set('repeatBy', repeatBy)
	}
}

const getExcludedAggregationTypes = (columnType) => {
	//#41424 exclude percentage
	const excludedAggregationTypes = [constants.PERCENTAGE]
	// #60041 removes list unique values aggregation for expenditure and notes columns
	if (columnType === reportConstants.REPORT_COLUMN_NOTES_COLUMN_TYPE_ID || 
		columnType === reportConstants.REPORT_COLUMN_EXPENDITURES_COLUMN_TYPE_ID) {
		excludedAggregationTypes.push(constants.LIST_UNIQUE_VALUES)
	}
	return excludedAggregationTypes
}

export default ColumnDataSourceForm
export { toggleScoredBreakdown, getExcludedAggregationTypes }
ColumnDataSourceForm.__filename__ = 'reports/common/column-data-source-form.js'
